import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AppRemixIcon,
  AppCard,
  AppInput,
  AppNoInfo,
  AppCheckbox,
  AppSingleSelect,
  AppNotificationAlert,
} from '@/layout/AppComponents';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import UserManagementModuleSidebar from '../../../layout/screenSpecifics/Modules/UserManagement/UserManagementModuleSidebar';
import Translator from '../../../layout/components/helpers/Translator';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import UserManagementPaginator from '../../../layout/screenSpecifics/Modules/UserManagement/UserManagementPaginator';
import errorHandler from '@/layout/components/error/ErrorHandler';
import UserManagementBase from './UserManagementBase';
import styled from 'styled-components';

export default function UserManagementUserAccessCodeScreen() {
  const { moduleBridgeHex, page, filtersHex } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const permissionsLevel = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level,
  );
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened,
  );
  const selectedUserAccessCodes = useSelector(
    (state) => state.currentPage?.selectedUserAccessCodes,
  );
  const userAccessCodeIDsPage = useSelector(
    (state) => state.currentPage?.userAccessCodeIDsPage,
  );
  const userAccessCodesPage = useSelector(
    (state) => state.currentPage?.userAccessCodesPage,
  );
  const pagination = useSelector((state) => state.currentPage?.pagination);
  const [loading, setLoading] = useState(true);
  const [autocloneOptions, setAutocloneOptions] = useState();
  const [validationSchema] = useState(
    Yup.object().shape({
      codes: Yup.string().required(
        ReactDOMServer.renderToString(
          <Translator file="admin" name="general.required" />,
        ),
      ),
      autoCloneID: Yup.number().required(
        ReactDOMServer.renderToString(
          <Translator file="admin" name="general.required" />,
        ),
      ),
    }),
  );

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  useEffect(() => {
    dispatch(currentPageAction.change('selectedUserAccessCodes', [], true));
  }, [page]);

  // > LOAD USER ACCESS CODES
  const loadUserAccessCodes = async () =>
    axios
      .post(axiosPath('api_r_user_module_userManagement_userAccessCode_list'), {
        moduleBridgeHex,
        filtersHex,
      })
      .then(({ data }) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change('pagination', data.pagination));
          dispatch(
            currentPageAction.change('userAccessCodesPage', data.elements, true),
          );
          dispatch(
            currentPageAction.change('userAccessCodeIDsPage', data.elementIDs, true),
          );
        }
      })
      .catch((e) => dispatch(errorHandler(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    setLoading(true);
    loadUserAccessCodes();
  }, [filtersHex]);
  // < LOAD USER ACCESS CODES

  // > LOAD AUTO CLONES
  const loadAutoclones = useCallback(async () => {
    await axios
      .post(
        axiosPath('api_r_user_module_userManagement_userAccessCode_autoclone_list'),
        {
          clientID: moduleData?.clientID,
        },
      )
      .then(({ data }) => {
        if (isMounted.current) {
          setAutocloneOptions(data);
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleData]);

  useEffect(() => {
    if (moduleData) loadAutoclones();
  }, [moduleData]);
  // < LOAD AUTO CLONES

  // > SAVE
  const handleSave = useCallback(
    async (values, { resetForm }) =>
      axios
        .post(axiosPath('api_r_user_module_userManagement_userAccessCode_add'), {
          moduleBridgeHex,
          ...values,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            AppNotificationAlert({
              title: ReactDOMServer.renderToString(
                <Translator file="admin" name="general.saved" />,
              ),
              message: data.message,
              type: 'success',
              duration: 0,
            });
            resetForm();
            setLoading(true);
            loadUserAccessCodes();
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex],
  );
  // < SAVE

  // > DELETE
  const handleDeleteUserAccessCodes = useCallback(
    async () =>
      axios
        .delete(axiosPath('api_r_user_module_userManagement_userAccessCode_delete'), {
          data: { moduleBridgeHex, selectedUserAccessCodes },
        })
        .then(() => {
          if (isMounted.current) {
            setLoading(true);
            navigate(
              path({
                name: 'user_module_userManagement_userAccessCode',
                params: {
                  moduleBridgeHex,
                  page: 1,
                  filtersHex,
                },
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, selectedUserAccessCodes, filtersHex],
  );
  // < DELETE

  const handleSelectedUserAccessCodes = useCallback(
    (id) => {
      let updatedSelectedUserAccessCodes = [...selectedUserAccessCodes];

      updatedSelectedUserAccessCodes = updatedSelectedUserAccessCodes?.includes(id)
        ? updatedSelectedUserAccessCodes?.filter((suac) => suac !== id)
        : [...updatedSelectedUserAccessCodes, id];

      dispatch(
        currentPageAction.change(
          'selectedUserAccessCodes',
          updatedSelectedUserAccessCodes,
          true,
        ),
      );
    },
    [selectedUserAccessCodes, dispatch],
  );

  const handleSelectedAllUserAccessCodes = useCallback(() => {
    const updatedSelectedUserAccessCodes =
      selectedUserAccessCodes?.length === userAccessCodesPage?.[page - 1]?.length
        ? []
        : userAccessCodeIDsPage?.[page - 1];

    dispatch(
      currentPageAction.change(
        'selectedUserAccessCodes',
        updatedSelectedUserAccessCodes,
        true,
      ),
    );
  }, [selectedUserAccessCodes, userAccessCodesPage, userAccessCodeIDsPage, dispatch]);

  return (
    <div>
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: moduleData?.moduleName,
                  route: {
                    name: 'user_module_userManagement_home',
                    params: {
                      moduleBridgeHex,
                    },
                  },
                },
                { name: 'Añadir codigos' },
              ]
            : [{ name: '...' }]
        }
      />
      <UserManagementBase />
      <UserManagementModuleSidebar
        permissionLevel={permissionsLevel}
        activeRoute="user_module_userManagement_userAccessCode"
      />
      {moduleData ? (
        <main role="main" className="col-md-12 col-lg-12 px-md-4">
          <div className={`${isSidebarOpened && 'sidebar-expanded'}`}>
            <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-0 mb-0 border-bottom">
              <AppCard header={<span>Añadir codigos</span>}>
                <Formik
                  validateOnChange={false}
                  validateOnBlur={false}
                  initialValues={{
                    codes: '',
                    autoCloneID: null,
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSave}
                >
                  {({
                    setFieldValue,
                    handleSubmit,
                    values,
                    errors,
                    isSubmitting,
                    resetForm,
                  }) => (
                    <>
                      <div className="row">
                        <div className="col-lg-12 col-md-6">
                          <AppInput
                            title={ReactDOMServer.renderToString(
                              <Translator file="admin" name="general.codes" />,
                            )}
                            icon={<AppRemixIcon icon="barcode" />}
                            onChange={(value) => setFieldValue('codes', value)}
                            value={values.codes}
                            error={errors.codes}
                            placeholder={ReactDOMServer.renderToString(
                              <Translator
                                file="admin"
                                name="client.enter-user-codes-separated-commas"
                              />,
                            )}
                            isClearable
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12 col-md-6">
                          <AppSingleSelect
                            options={autocloneOptions}
                            onChange={(opt) => setFieldValue('autoCloneID', opt)}
                            title={ReactDOMServer.renderToString(
                              <Translator file="admin" name="general.module" />,
                            )}
                            icon={<AppRemixIcon icon="apps-2" />}
                            placeholder={ReactDOMServer.renderToString(
                              <Translator
                                file="admin"
                                name="general.select-autocloner"
                              />,
                            )}
                            value={values.autoCloneID}
                            error={errors?.autoCloneID}
                            isLoading={!autocloneOptions}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-lg-12 col-md-6">
                          {selectedUserAccessCodes?.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger mr-2"
                              onClick={handleDeleteUserAccessCodes}
                            >
                              <AppRemixIcon icon="delete-bin-6" /> Eliminar códigos
                              seleccionados
                            </button>
                          )}
                          {filtersHex !== '0' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                navigate(
                                  path({
                                    name: 'user_module_userManagement_userAccessCode',
                                    params: {
                                      moduleBridgeHex,
                                    },
                                  }),
                                )
                              }
                            >
                              <AppRemixIcon icon="delete-bin-6" /> Eliminar filtros
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-success float-right"
                            type="submit"
                            onClick={handleSubmit}
                            disabled={loading || isSubmitting}
                          >
                            <Translator file="admin" name="general.add" />
                          </button>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12 mt-5">
                          {loading ? (
                            <AppNoInfo />
                          ) : (
                            <>
                              {userAccessCodesPage?.length > 0 ? (
                                <>
                                  <table className="table table-hover mb-0">
                                    <thead>
                                      <tr>
                                        <Th className="col-1">
                                          <AppCheckbox
                                            value={
                                              selectedUserAccessCodes?.length ===
                                              userAccessCodesPage?.[page - 1]?.length
                                            }
                                            containerClassName="mt-0 mb-0"
                                            onChange={
                                              handleSelectedAllUserAccessCodes
                                            }
                                          />
                                        </Th>
                                        <Th className="col-4">Código</Th>
                                        <Th className="col-3">Módulo</Th>
                                        <Th className="col-2 text-center">
                                          Fecha añadido
                                        </Th>
                                        <Th className="col-2 text-center">
                                          Fecha registrado
                                        </Th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {userAccessCodesPage?.[page - 1]?.map((uac) => (
                                        <tr
                                          key={uac.id}
                                          onClick={() =>
                                            handleSelectedUserAccessCodes(uac.id)
                                          }
                                        >
                                          <Td className="col-1">
                                            <AppCheckbox
                                              value={selectedUserAccessCodes?.includes(
                                                uac.id,
                                              )}
                                              containerClassName="mt-0 mb-0"
                                              onChange={() => {}}
                                            ></AppCheckbox>
                                          </Td>
                                          <Td className="col-4">{uac?.code}</Td>
                                          <Td className="col-3">{uac?.moduleName}</Td>
                                          <Td className="col-2 text-center">
                                            {uac?.date}
                                          </Td>
                                          <Td className="col-2 text-center">
                                            {uac?.createdDate || '-'}
                                          </Td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {pagination?.last && pagination?.last >= 2 && (
                                    <UserManagementPaginator
                                      pagination={pagination}
                                      route="user_module_userManagement_userAccessCode"
                                      page={page}
                                      plantObligationPages={
                                        userAccessCodeIDsPage ?? []
                                      }
                                      itemsPerPage={100}
                                      className="mt-3"
                                      params={{
                                        moduleBridgeHex,
                                        page,
                                        filtersHex,
                                      }}
                                    />
                                  )}
                                </>
                              ) : (
                                <div className="alert alert-light">
                                  <Translator
                                    file="admin"
                                    name="client.no-user-codes"
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </Formik>
              </AppCard>
            </div>
          </div>
        </main>
      ) : (
        <AppNoInfo />
      )}
    </div>
  );
}

const Th = styled.th`
  vertical-align: baseline !important;
  line-height: 0;
`;

const Td = styled.td`
  vertical-align: baseline !important;
  line-height: 0;
`;
